import React, { useState } from 'react';

export function formatPrice(cents, currency = 'usd') {
  if (cents == null) return '$0';
  const amount = (cents / 100).toFixed(2);
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  return `${symbol}${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export function formatDate(unixSeconds) {
  if (!unixSeconds) return '—';
  const d = new Date(unixSeconds * 1000);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Stripe billing cadence: (interval=month, interval_count=12) means once a year.
// Normalize the common cases; fall back to "every N <unit>s" for unusual cadences.
export function formatInterval(interval, intervalCount) {
  const n = Number(intervalCount) || 1;
  if (!interval) return 'period';
  if (n === 1) return interval;
  if (interval === 'month' && n === 12) return 'year';
  if (interval === 'month' && n === 6)  return '6 months';
  if (interval === 'month' && n === 3)  return 'quarter';
  return `${n} ${interval}s`;
}

function StatusPill({ status }) {
  const styles = status === 'paid'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status === 'open'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize border ${styles}`}>
      {status}
    </span>
  );
}

function SubscriptionStatusBadge({ status }) {
  const active = ['active', 'trialing'].includes(status);
  const warn = status === 'past_due';
  const styles = active
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : warn
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize border ${styles}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function SubscriptionsCard({ subscriptions, onCancel, onResume }) {
  const [pendingCancel, setPendingCancel] = useState(null);

  if (!subscriptions || subscriptions.length === 0) return null;

  const confirmCancel = () => {
    if (!pendingCancel) return;
    onCancel?.(pendingCancel.id);
    setPendingCancel(null);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
      </div>

      <div className="space-y-3">
        {subscriptions.map(sub => {
          const active = ['active', 'trialing', 'past_due'].includes(sub.status);
          return (
            <div key={sub.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900">
                    {sub.product_name || 'Subscription'}
                    {sub.quantity > 1 && <span className="text-gray-400 font-normal"> × {sub.quantity}</span>}
                  </p>
                  <SubscriptionStatusBadge status={sub.status} />
                  {sub.cancel_at_period_end && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                      Cancels at period end
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {sub.amount_cents != null && (() => {
                    const gross = sub.amount_cents * (sub.quantity || 1);
                    const net = sub.net_amount_cents != null ? sub.net_amount_cents : gross;
                    const discounted = net < gross;
                    return (
                      <>
                        {discounted && (
                          <span className="line-through text-gray-400 mr-1">{formatPrice(gross, sub.currency)}</span>
                        )}
                        {formatPrice(net, sub.currency)} / {formatInterval(sub.interval, sub.interval_count)}
                        {discounted && sub.discount_label && (
                          <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {sub.discount_label}
                          </span>
                        )}
                      </>
                    );
                  })()}
                  {sub.current_period_end && (
                    <> · {sub.cancel_at_period_end ? 'Ends' : 'Renews'} {formatDate(sub.current_period_end)}</>
                  )}
                </p>
              </div>
              {active && (
                sub.cancel_at_period_end ? (
                  <button onClick={() => onResume?.(sub.id)}
                    className="shrink-0 px-3.5 py-2 text-sm font-semibold border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer transition">
                    Resume
                  </button>
                ) : (
                  <button onClick={() => setPendingCancel(sub)}
                    className="shrink-0 px-3.5 py-2 text-sm font-semibold border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-50 cursor-pointer transition">
                    Cancel
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>

      {pendingCancel && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setPendingCancel(null)} />
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-rose-50 text-rose-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancel subscription?</h3>
                </div>
                <button onClick={() => setPendingCancel(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                Your <span className="font-semibold text-gray-900">{pendingCancel.product_name || 'subscription'}</span> will remain active until the end of the current billing period
                {pendingCancel.current_period_end && (
                  <> on <span className="font-semibold text-gray-900">{formatDate(pendingCancel.current_period_end)}</span></>
                )}
                . You won't be charged again, and you can resume any time before that date.
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button type="button" onClick={() => setPendingCancel(null)}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer transition">
                  Keep subscription
                </button>
                <button type="button" onClick={confirmCancel}
                  className="flex-1 rounded-lg bg-rose-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 cursor-pointer shadow-sm transition">
                  Cancel subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function InvoicesCard({ invoices }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
      </div>

      {invoices && invoices.length > 0 ? (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <tr>
                <th className="py-2.5 px-2 font-medium">Date</th>
                <th className="py-2.5 px-2 font-medium">Number</th>
                <th className="py-2.5 px-2 font-medium">Amount</th>
                <th className="py-2.5 px-2 font-medium">Status</th>
                <th className="py-2.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition">
                  <td className="py-3 px-2 text-gray-900">{formatDate(inv.created)}</td>
                  <td className="py-3 px-2 text-gray-600 font-mono text-xs">{inv.number || inv.id.slice(0, 12)}</td>
                  <td className="py-3 px-2 text-gray-900 font-semibold">{formatPrice(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                  <td className="py-3 px-2"><StatusPill status={inv.status} /></td>
                  <td className="py-3 px-2">
                    <div className="flex gap-3">
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 hover:text-indigo-800">View</a>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 hover:text-indigo-800">PDF</a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No invoices yet.</p>
        </div>
      )}
    </div>
  );
}

export default function BillingSection({ subscriptions, invoices, onCancel, onResume }) {
  return (
    <>
      <SubscriptionsCard subscriptions={subscriptions} onCancel={onCancel} onResume={onResume} />
      <InvoicesCard invoices={invoices} />
    </>
  );
}
